class WPMapMarker extends HTMLElement {
  static get is() {
    return 'wp-map-marker';
  }

  static get observedAttributes() {
    return ['active', 'bounce', 'hidden', 'open'];
  }

  get hidden() {
    return this.hasAttribute('hidden');
  }

  set hidden(value) {
    if (Boolean(value)) {
      this.setAttribute('hidden', '');
    } else {
      this.removeAttribute('hidden');
    }
  }

  get bounce() {
    return this.hasAttribute('bounce');
  }

  set bounce(value) {
    if (Boolean(value)) {
      this.setAttribute('bounce', '');
    } else {
      this.removeAttribute('bounce');
    }
  }

  get latitude() {
    return this.hasAttribute('latitude')
      ? parseFloat(this.getAttribute('latitude'))
      : 0;
  }

  set latitude(value) {
    this.setAttribute('latitude', parseFloat(value));
    this._updatePosition();
  }

  get longitude() {
    return this.hasAttribute('longitude')
      ? parseFloat(this.getAttribute('longitude'))
      : 0;
  }

  set longitude(value) {
    this.setAttribute('longitude', parseFloat(value));
  }

  get open() {
    return this.hasAttribute('open');
  }

  set open(value) {
    if (Boolean(value)) {
      this.setAttribute('open', '');
    } else {
      this.removeAttribute('open');
    }
  }

  get active() {
    return this.hasAttribute('active');
  }

  set active(value) {
    if (Boolean(value)) {
      this.setAttribute('active', '');
    } else {
      this.removeAttribute('active');
    }
  }

  get postID() {
    return this.hasAttribute('postID')
      ? parseInt(this.getAttribute('postID'))
      : 0;
  }

  set postID(value) {
    this.setAttribute('postID', parseInt(value));
  }

  /**
   * Getter for `icon` attribute
   */
  get icon() {
    return this.hasAttribute('icon') ? this.getAttribute('icon') : null;
  }

  /**
   * Setter for `icon` attribute
   */
  set icon(value) {
    if (String(value.length)) {
      this.setAttribute('icon', value);
    } else {
      this.removeAttribute('icon');
    }
  }

  /**
   * Getter for `width` attribute
   */
  get width() {
    return this.hasAttribute('width')
      ? parseInt(this.getAttribute('width'))
      : 0;
  }

  /**
   * Setter for `width` attribute
   */
  set width(value) {
    this.setAttribute('width', parseInt(value));
  }

  /**
   * Getter for `height` attribute
   */
  get height() {
    return this.hasAttribute('height')
      ? parseInt(this.getAttribute('height'))
      : 0;
  }

  /**
   * Setter for `height` attribute
   */
  set height(value) {
    this.setAttribute('height', parseInt(value));
  }

  constructor(self) {
    self = super(self);
    this._mapEl = this.parentNode;
    this.map = null;
    this.marker = null;
    return self;
  }

  connectedCallback() {
    this._mapEl = this.parentNode;

    // If not parent element, return
    if (!this._mapEl) {
      return;
    }

    this.style.display = 'none';

    // If map already exists, add marker, else add event listener to add map
    if (this._mapEl.map) {
      this.map = this._mapEl.map;
      this._initMarker();
    } else {
      this._mapEl.addEventListener(
        'mapready',
        this._initMarker.bind(this),
        true,
      );
    }
  }

  attributeChangedCallback(name, oldVal, newVal) {
    switch (name) {
      case 'hidden':
        this._hiddenChanged();
      case 'open':
        this._setInfoWindow();
      case 'active':
        this._activeChanged();
      case 'bounce':
        this._bounceChanged();
    }
  }

  disconnectedCallback() {
    this._mapEl.removeEventListener(
      'mapready',
      this._initMarker.bind(this),
      true,
    );

    if (this.map) {
      this.map.markers.splice(this.map.markers.indexOf(this.marker), 1);
    }
  }

  _initMarker(evt) {
    if (evt) {
      this.map = evt.detail.map;
    }

    // If map not defined, return
    if (!this.map) {
      return;
    }

    // If latitude/longitude/map is not set, return
    if (!this.latitude || !this.longitude || !this.map) {
      return;
    }

    const options = {
      position: {
        lat: parseFloat(this.latitude),
        lng: parseFloat(this.longitude),
      },
      map: this.map,
      zIndex: 1000,
    };

    if (this.icon) {
      if (this.width && this.height) {
        options.icon = {
          url: this.icon,
          scaledSize: new google.maps.Size(this.width, this.height),
        };
      } else {
        options.icon = this.icon;
      }
    }

    // Create a marker
    this.marker = new google.maps.Marker(options);

    // Push marker to map array
    this.map.markers
      ? this.map.markers.push(this.marker)
      : (this.map.markers = [this.marker]);

    google.maps.event.addListener(this.marker, 'click', (evt) => {
      this.click();

      // if marker contains HTML, show info window when marker is clicked
      if (this.innerHTML) {
        this.open = true;
      }
    });

    // Fire event
    this.dispatchEvent(
      new CustomEvent('mapchanged', {
        bubbles: true,
        composed: true,
      }),
    );

    // Hide element
    this.style.display = 'none';

    // Check if we need infowindow open on init
    this._setInfoWindow();
  }

  /**
   * Set the visibility of the infoWindow
   * @returns {undefined}
   * @memberof WPMapMarker
   */
  _setInfoWindow() {
    if (this.open) {
      this._openInfoWindow();
    } else {
      this._closeInfoWindow();
    }
  }

  /**
   * Dispatch event to open infoWindow on `google-map` element
   * @returns {undefined}
   * @memberof WPMapMarker
   */
  _openInfoWindow() {
    this.dispatchEvent(
      new CustomEvent('infowindowopen', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Dispatch event to close infoWindow on `google-map` element
   * @returns {undefined}
   * @memberof WPMapMarker
   */
  _closeInfoWindow() {
    this.dispatchEvent(
      new CustomEvent('infowindowclosed', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Update the position of the marker
   * @returns {undefined}
   * @memberof WPMapMarker
   */
  _updatePosition() {
    if (this.marker && this.latitude !== null && this.longitude !== null) {
      this.marker.setPosition(
        new google.maps.LatLng(
          parseFloat(this.latitude),
          parseFloat(this.longitude),
        ),
      );

      // Fire event
      this.dispatchEvent(
        new CustomEvent('mapchanged', {
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  _activeChanged() {
    if (!this.marker) {
      return;
    }

    if (this.active) {
      // Increase size of marker icon
      this.marker.setIcon({
        url: this.icon,
        scaledSize: new google.maps.Size(60, 60),
      });
    } else {
      if (this.width && this.height) {
        this.marker.setIcon({
          url: this.icon,
          scaledSize: new google.maps.Size(30, 30),
        });
      } else {
        this.marker.setIcon(this.icon);
      }
    }
  }

  _hiddenChanged() {
    if (!this.marker) {
      return;
    }

    if (this.hidden) {
      this.marker.setVisible(false);
    } else {
      this.marker.setVisible(true);
    }
  }

  _bounceChanged() {
    if (!this.marker) {
      return;
    }

    if (this.bounce) {
      this.marker.setAnimation(google.maps.Animation.BOUNCE);

      setTimeout(() => {
        this.marker.setAnimation(null);
        this.bounce = false;
      }, 50);
    }
  }
}

customElements.define(WPMapMarker.is, WPMapMarker);
